import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime, Schema } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type { ApiInfrastructureErrors } from '../../../data/error/api';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import type { FavouriteListId } from '@vs-shop/schemas/branded/MiscSchema';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = `/api/v2/shop/product-favorites/list/{uuid}/products/expand`;
const HTTP_METHOD = 'get';

export type GetFavouriteListProductsProps = {
  readonly uuid: typeof FavouriteListId.Type;
  readonly page: number;
  readonly itemsPerPage: number;
};

export type MakeGetFavouriteListProductsDeps<ProductBaseSchema extends Schema.Top> = {
  readonly productBase: ProductBaseSchema;
};

export const makeGetFavouriteListProducts = <ProductBaseSchema extends Schema.Top>(
  deps: MakeGetFavouriteListProductsDeps<ProductBaseSchema>
) => {
  const GetFavouriteListProductsError = createBackendApiError({
    domain: 'ProductFavouriteList',
    endpoint: 'GetProducts',
    path: ENDPOINT,
    identifierField: 'uuid',
  });

  const getFavouriteListProductsFetch = ({
    uuid,
    page,
    itemsPerPage,
  }: GetFavouriteListProductsProps) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          params: { uuid },
          query: { page, itemsPerPage },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'productFavouriteList.uuid': uuid,
          'productFavouriteList.page': page,
          'productFavouriteList.itemsPerPage': itemsPerPage,
        },
      })
    );

  const getFavouriteListProducts = (props: GetFavouriteListProductsProps) =>
    getFavouriteListProductsFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.productBase))(response),
          404: (response) =>
            Effect.fail(
              new GetFavouriteListProductsError.NotFoundError({ uuid: props.uuid, response })
            ),
          orElse: (response) =>
            Effect.fail(
              new GetFavouriteListProductsError.ApiRequestError({ uuid: props.uuid, response })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductFavouriteListGetProducts,
          'Debug',
          `Successfully resolved ${String(result['hydra:totalItems'])} products (page ${String(props.page)}) of favourite list ${props.uuid}`
        )
      ),
      Effect.withSpan('api.productFavouriteList.getProducts', {
        kind: 'internal',
        attributes: {
          'productFavouriteList.uuid': props.uuid,
          'productFavouriteList.page': props.page,
          'productFavouriteList.itemsPerPage': props.itemsPerPage,
        },
      })
    );

  type GetFavouriteListProductsRequest = {
    readonly _tag: 'GetFavouriteListProductsRequest';
    readonly uuid: typeof FavouriteListId.Type;
    readonly page: number;
    readonly itemsPerPage: number;
  } & Request.Request<
    HydraCollectionResponseType<ProductBaseSchema>,
    | InstanceType<typeof GetFavouriteListProductsError.NotFoundError>
    | InstanceType<typeof GetFavouriteListProductsError.ApiRequestError>
    | ApiInfrastructureErrors,
    BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
  >;

  const GetFavouriteListProductsRequest = Request.tagged<GetFavouriteListProductsRequest>(
    'GetFavouriteListProductsRequest'
  );
  const GetFavouriteListProductsResolver = RequestResolver.fromEffect(
    liftRequest((request: GetFavouriteListProductsRequest) =>
      getFavouriteListProducts({
        uuid: request.uuid,
        page: request.page,
        itemsPerPage: request.itemsPerPage,
      })
    )
  );

  const requestFavouriteListProducts = (props: GetFavouriteListProductsProps) =>
    Effect.request(GetFavouriteListProductsRequest(props), GetFavouriteListProductsResolver);

  return { GetFavouriteListProductsError, requestFavouriteListProducts };
};
